import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Le Valmier collection landing pages (sections lv-lp-*).
 *
 * <lv-lp-add>  Adds one single-variant piece to the bag through the AJAX Cart API and
 *              dispatches the theme's standard CartLinesUpdateEvent, the same contract as
 *              assets/lv-cart-upsell.js, so the cart drawer re-renders and opens by itself.
 * <lv-lp-nav>  Sticky in-page navigation: marks the group in view and scrolls smoothly
 *              (instantly when the visitor prefers reduced motion).
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function sectionIds() {
  const ids = new Set();
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

function announce(from, message) {
  const status = from.closest('.shopify-section')?.querySelector('[data-lvlp-status]');
  if (!(status instanceof HTMLElement)) return;
  status.textContent = '';
  window.requestAnimationFrame(() => {
    status.textContent = message;
  });
}

class LvLpAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  #busy = false;
  #timer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    window.clearTimeout(this.#timer);
  }

  get #button() {
    const button = this.querySelector('button');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('button')) return;
    event.preventDefault();
    this.#add();
  };

  #setLabel(text) {
    const button = this.#button;
    if (button) button.textContent = text;
  }

  #restore(delay) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      const button = this.#button;
      if (!button) return;
      button.classList.remove('is-added');
      button.textContent = button.dataset.label || button.textContent;
    }, delay);
  }

  async #add() {
    const button = this.#button;
    const variantId = this.dataset.variantId;
    if (!button || !variantId || this.#busy) return;

    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      const cart = await fetchCart();

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'lv-lp-add', didError: true },
        });
        const message = result.description || result.message || 'This piece could not be added.';
        this.#setLabel(message);
        announce(this, message);
        this.#restore(3500);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: 1, source: 'lv-lp-add', sections: result.sections, didError: false },
      });
      button.classList.add('is-added');
      this.#setLabel(button.dataset.added || 'Added');
      announce(this, `${this.dataset.title || ''} ${button.dataset.added || 'Added'}`.trim());
      this.#restore(2400);
    } catch (error) {
      deferred.reject(error);
      this.#setLabel('Please try again');
      this.#restore(3000);
      if (error?.name !== 'AbortError') console.warn('[lv-lp-add] Add to cart failed:', error);
    } finally {
      this.#busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

class LvLpNav extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });

    const links = this.#links;
    const targets = links.map((link) => document.getElementById(link.hash.slice(1))).filter(Boolean);
    if (!targets.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          for (const link of links) link.setAttribute('aria-current', String(link.hash === `#${entry.target.id}`));
          const active = links.find((link) => link.getAttribute('aria-current') === 'true');
          if (active && this.scrollWidth > this.clientWidth) {
            this.scrollTo({ left: active.offsetLeft - 20, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        }
      },
      { rootMargin: '-35% 0px -55% 0px' }
    );
    for (const target of targets) this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {HTMLAnchorElement[]} */
  get #links() {
    return Array.from(this.querySelectorAll('a[href^="#"]')).filter((link) => link instanceof HTMLAnchorElement);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
  };
}

/*
 * The store header is sticky, so the group navigation sits right under it and every
 * in-page target keeps clear of both when scrolled to.
 */
function syncOffsets() {
  const header = document.querySelector('#header-component');
  const headerHeight = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 0;
  const nav = document.querySelector('lv-lp-nav');
  if (nav instanceof HTMLElement) nav.style.setProperty('--lvlp-sticky-top', `${Math.max(0, headerHeight - 1)}px`);
  const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 0;
  for (const target of document.querySelectorAll('.lvlp [id]')) {
    if (!(target instanceof HTMLElement)) continue;
    const extra = target.classList.contains('lvlp-group') ? navHeight : 0;
    target.style.scrollMarginTop = `${headerHeight + extra + 12}px`;
  }
}

/* Smooth scrolling for the in-page buttons (hero, tiles) on the same landing page. */
if (!window.__lvLpAnchors) {
  window.__lvLpAnchors = true;
  syncOffsets();
  window.addEventListener('load', syncOffsets, { once: true });
  window.addEventListener('resize', syncOffsets, { passive: true });
  document.addEventListener('shopify:section:load', syncOffsets);
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('.lvlp a[href^="#"]:not(lv-lp-nav a)') : null;
    if (!(link instanceof HTMLAnchorElement) || link.hash.length < 2) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
  });
}

if (!customElements.get('lv-lp-add')) customElements.define('lv-lp-add', LvLpAdd);
if (!customElements.get('lv-lp-nav')) customElements.define('lv-lp-nav', LvLpNav);
